'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchMode: () => void;
}

export default function LoginForm({ onSwitchMode }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back. Your world awaits.');
      router.push('/editorial-discovery-feed');
    } catch (err: any) {
      setError('email', {
        message: err?.message || 'Invalid credentials. Please check your email and password.',
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="animate-slide-up">
      <div className="mb-8">
        <h1 className="font-editorial text-2xl font-semibold text-cream">
          Welcome back
        </h1>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Your private world of extraordinary discovery continues where you left it.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Email */}
        <div>
          <label htmlFor="login-email" className="block text-xs tracking-widest uppercase text-muted-foreground mb-2">
            Member Email
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="your@email.com"
            className="input-luxury w-full px-4 py-3 rounded-sm text-sm"
            {...register('email', {
              required: 'Member email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="text-red-400 text-xs mt-1.5">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="login-password" className="block text-xs tracking-widest uppercase text-muted-foreground mb-2">
            Passphrase
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Your secure passphrase"
              className="input-luxury w-full px-4 py-3 pr-12 rounded-sm text-sm"
              {...register('password', {
                required: 'Passphrase is required',
                minLength: { value: 6, message: 'Passphrase must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide passphrase' : 'Show passphrase'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-cream transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-400 text-xs mt-1.5">{errors.password.message}</p>
          )}
        </div>

        {/* Remember */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              className="w-4 h-4 rounded-sm border border-muted bg-input accent-gold"
              {...register('rememberMe')}
            />
            <span className="text-xs text-muted-foreground group-hover:text-cream transition-colors">
              Keep me signed in
            </span>
          </label>
          <button
            type="button"
            className="text-xs text-gold hover:text-gold-light transition-colors tracking-wide"
          >
            Forgot passphrase?
          </button>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3.5 gold-gradient-bg text-obsidian font-semibold text-sm tracking-widest uppercase rounded-sm transition-all duration-200 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-obsidian/30 border-t-obsidian rounded-full animate-spin" />
              <span>Entering...</span>
            </>
          ) : (
            <>
              <LogIn size={16} />
              <span>Enter Your World</span>
            </>
          )}
        </button>
      </form>

      {/* Switch mode */}
      <p className="text-center text-xs text-muted-foreground mt-6">
        Not yet a member?{' '}
        <button
          onClick={onSwitchMode}
          className="text-gold hover:text-gold-light transition-colors"
        >
          Apply for Membership
        </button>
      </p>

      {/* Demo Credentials Box */}
      <div className="mt-8 border border-muted rounded-sm overflow-hidden">
        <div className="px-4 py-2.5 bg-muted/30 border-b border-muted">
          <span className="text-xs tracking-widest uppercase text-muted-foreground">
            Demo Access
          </span>
        </div>
        <div className="px-4 py-3 space-y-1">
          <p className="text-xs text-muted-foreground">Email: <span className="text-cream font-mono">member@privateworld.com</span></p>
          <p className="text-xs text-muted-foreground">Password: <span className="text-cream font-mono">Member2026!</span></p>
        </div>
      </div>
    </div>
  );
}